import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import {
  createUserRole,
  selectSelectedUserRole,
  setSelectedUserRole,
  updateUserRole,
} from "../../../../pages/Users";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { Modal } from "../../../molecules";
import { MODAL_SIZE } from "../../AppModals/modals.constants";
import { REGEX, EMPTY_STRING } from "../../../../constants";
import { isEqual } from "lodash";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { VALIDATION_MESSAGES } from "../../../../constants";

const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
} = REGEX;
const { setSelectedModal } = modalActions;
const { UPDATE_USER_ROLE, CREATE_USER_ROLE } = SPINNER_NAMES;
const { NAME_SHOULD_CONTAIN, NAME_SHOULD_NOT_CONTAIN, MIN_CHAR_LENGTH, MAX_CHAR_LENGTH_TWENTY_ONE, 
  NAME_SHOULD_NOT_CONTAIN_AT_END, USER_ROLE_REQUIRED } = VALIDATION_MESSAGES

const ManageUserRoleModal = () => {
  const dispatch = useDispatch();
  const selectedUserRole = useSelector(selectSelectedUserRole);

  const [userRole, setUserRole] = useState({
    name: EMPTY_STRING,
  });
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  useEffect(() => {
    if (selectedUserRole) setUserRole(selectedUserRole);
  }, [selectedUserRole]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(
        SPECIAL_CHARACTERS,
        NAME_SHOULD_NOT_CONTAIN
      )
      .min(3, MIN_CHAR_LENGTH)
      .max(21,MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .required(USER_ROLE_REQUIRED),
  });

  const checkIsSubmitButtonDisabled = (isValid, values) =>
    !isValid || isEqual(userRole, values) || isSpinnerActive;

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
    dispatch(setSelectedUserRole(null));
  }, [dispatch]);

  const handleOnSubmit = useCallback(
    (values) => {
      dispatch(
        selectedUserRole
          ? updateUserRole({
              userRoleDetails: values,
              id: selectedUserRole.id,
            })
          : createUserRole({
              userRoleDetails: values,
            })
      ).then((res) => {
        if (res.payload) handleClose();
      });
    },
    [dispatch, handleClose, selectedUserRole]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={userRole}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({
        values,
        isValid,
        handleSubmit,
        handleChange,
        handleBlur,
        handleReset,
        touched,
        errors,
      }) => {
        const footer = (
          <div className="p-6 space-x-1 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn btn-primary-light"
              type="button"
              onClick={() => handleClose()}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={checkIsSubmitButtonDisabled(isValid, values)}
              onClick={handleSubmit}
            >
              <Spinner
                name={selectedUserRole ? UPDATE_USER_ROLE : CREATE_USER_ROLE}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {selectedUserRole ? "Update User Role" : "Submit"}
              </Spinner>
            </button>
          </div>
        );
        return (
          <Modal
            title={selectedUserRole ? "Update User Role" : "Manage User Role"}
            subtitle={`${selectedUserRole ? "Update" : "Configure"} 
            the user role settings.`}
            size={MODAL_SIZE.FULL}
            handleReset={handleReset}
            handleClose={handleClose}
            footer={footer}
          >
            <Form>
              <div className="modal-body p-6 flex-1  overflow-auto">
                <div className="md:w-4/12 mb-6">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      User Role Name
                    </label>
                    <Field
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-red-600":
                        touched.name && errors.name,
                      })}
                      placeholder="User Role Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <div className="font-medium text-red-500 text-sm absolute">
                        {errors.name}
                      </div>
                    )}
                  </div>
                </div>
                <h6 className="text-base text-gray-50 font-bold mb-4">
                  Object Access
                </h6>
                <div className="table-responsive border border-gray-600 rounded-lg">
                  <table className="table text-gray-50 w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-600 bg-gray-900 text-left">
                        <th className="px-6 py-3 text-xs font-medium tracking-wider uppercase rounded-tl-lg">
                          Objects
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider uppercase">
                          View
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider uppercase">
                          Edit
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider uppercase rounded-tr-lg">
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600 bg-gray-800">
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium rounded-bl-lg">
                          Object Name
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <select className="bg-transparent text-center h-auto border-0 p-0 pr-8 text-sm font-normal focus:ring-0">
                            <option>Everything</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <select className="bg-transparent text-center h-auto border-0 p-0 pr-8 text-sm font-normal focus:ring-0">
                            <option>Read-Only</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium rounded-br-lg">
                          <select className="bg-transparent text-center h-auto border-0 p-0 pr-8 text-sm font-normal focus:ring-0">
                            <option>None</option>
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default ManageUserRoleModal;
